import React, { useState } from 'react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc, setDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { K, inputStyle } from '../constants';
import { Btn } from '../components/ui';
import { ArrowLeft } from 'lucide-react';

const ALLOWED_DOMAINS = ['cmfe.org'];

export function AuthModal({ onClose }) {
  const [mode, setMode] = useState('login'); // 'login' | 'signup' | 'request' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  const switchMode = (m) => { setMode(m); setError(''); setResetSent(false); setRequestSent(false); };

  const submit = async () => {
    setError('');
    setLoading(true);
    try {
      if (mode === 'signup') {
        const cleanEmail = email.trim().toLowerCase();
        const domain = cleanEmail.split('@')[1];
        let allowed = !!domain && ALLOWED_DOMAINS.includes(domain);
        // Non-department emails may still sign up if an admin has approved their
        // access request (a targeted get on their own email — no list access).
        if (!allowed && cleanEmail) {
          try {
            const snap = await getDoc(doc(db, 'allowedEmails', cleanEmail));
            allowed = snap.exists();
          } catch (e) {
            // Couldn't reach the allowlist — this is a connectivity problem, not a
            // rejection. Say so plainly, otherwise an already-approved user is
            // wrongly told they were never approved.
            console.error('[AuthModal] allowlist check failed:', e);
            setError('Could not verify your access right now. Check your connection and try again.');
            setLoading(false);
            return;
          }
        }
        if (!allowed) {
          setError('Sign-up is restricted to @cmfe.org emails. If you were invited, use "Request access" below and an admin can approve you.');
          setLoading(false);
          return;
        }
        const cred = await createUserWithEmailAndPassword(auth, cleanEmail, password);
        try {
          await setDoc(doc(db, 'users', cred.user.uid), {
            email: cleanEmail,
            role: 'member',
            createdAt: serverTimestamp(),
          });
        } catch (docErr) {
          // The auth account was created but the profile doc write failed — roll
          // back the orphaned account so the user can cleanly retry instead of
          // ending up signed in with no profile (and unmanageable in AdminPanel).
          console.error('[AuthModal] profile create failed, rolling back account:', docErr);
          try { await cred.user.delete(); } catch { /* best effort */ }
          setError('Could not finish creating your account. Please try again.');
          setLoading(false);
          return;
        }
        onClose();
      } else if (mode === 'login') {
        await signInWithEmailAndPassword(auth, email, password);
        onClose();
      } else if (mode === 'request') {
        const cleanEmail = email.trim().toLowerCase();
        if (!name.trim()) { setError('Please enter your name.'); setLoading(false); return; }
        if (!cleanEmail || !cleanEmail.includes('@')) { setError('Please enter a valid email address.'); setLoading(false); return; }
        try {
          await addDoc(collection(db, 'accessRequests'), {
            name: name.trim().slice(0, 100),
            email: cleanEmail.slice(0, 200),
            reason: reason.trim().slice(0, 500),
            status: 'pending',
            createdAt: serverTimestamp(),
          });
          setRequestSent(true);
        } catch (reqErr) {
          console.error('[AuthModal] access request failed:', reqErr);
          setError('Could not submit your request. Check your connection and try again.');
        }
        setLoading(false);
        return;
      } else {
        await sendPasswordResetEmail(auth, email);
        setResetSent(true);
      }
    } catch (e) {
      console.error('[AuthModal] auth error:', e.code, e.message, e);
      setError(friendlyError(e.code, e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 400, background: '#000000cc' }} />

      <div className="hc-modal-sheet" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 401, background: K.bg1, borderRadius: '16px 16px 0 0', padding: 24, maxWidth: 480, margin: '0 auto' }}>
        <div style={{ width: 40, height: 4, background: K.bdr2, borderRadius: 2, margin: '0 auto 20px' }} />

        {mode === 'reset' ? (
          <>
            <div style={{ fontFamily: K.fSans, fontSize: 22, fontWeight: 700, color: K.t0, marginBottom: 6 }}>Reset Password</div>
            <div style={{ fontSize: 11, color: K.t2, marginBottom: 20 }}>Enter your email and we'll send a reset link.</div>

            {resetSent ? (
              <div style={{ background: `${K.ok}1a`, border: `1px solid ${K.ok}`, borderRadius: 8, padding: 14, marginBottom: 16, fontSize: 12, color: K.ok }}>
                Reset email sent — check your inbox.
              </div>
            ) : (
              <>
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                  style={{ ...inputStyle(), marginBottom: 16 }}
                  autoComplete="email"
                />
                {error ? <div style={{ fontSize: 11, color: K.bad, marginBottom: 12 }}>{error}</div> : null}
                <Btn onClick={submit} col={K.red} block>{loading ? 'Sending...' : 'Send Reset Email'}</Btn>
              </>
            )}

            <button onClick={() => switchMode('login')} style={{ width: '100%', marginTop: 10, background: 'none', border: 'none', color: K.t3, cursor: 'pointer', fontSize: 11, fontFamily: K.fSans, padding: '8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <ArrowLeft size={14} color={K.t3} strokeWidth={1.75} /> Back to log in
            </button>
          </>
        ) : mode === 'request' ? (
          <>
            <div style={{ fontFamily: K.fSans, fontSize: 22, fontWeight: 700, color: K.t0, marginBottom: 6 }}>Request Access</div>
            <div style={{ fontSize: 11, color: K.t2, marginBottom: 20, lineHeight: 1.5 }}>
              Don't have a department (@cmfe.org) email? Tell us who you are and an administrator will review your request.
            </div>

            {requestSent ? (
              <>
                <div style={{ background: `${K.ok}1a`, border: `1px solid ${K.ok}`, borderRadius: 8, padding: 14, marginBottom: 16, fontSize: 12, color: K.ok, lineHeight: 1.6 }}>
                  Request submitted. An administrator will review it. Once you're approved, come back and tap <strong>Sign Up</strong> to finish creating your account with this email.
                </div>
                <Btn onClick={() => switchMode('login')} col={K.red} block>Done</Btn>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
                  <input
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    style={inputStyle()}
                    autoComplete="name"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={inputStyle()}
                    autoComplete="email"
                  />
                  <textarea
                    placeholder="Why do you need access? (optional)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    style={{ ...inputStyle(), resize: 'vertical', minHeight: 72, fontFamily: K.fSans }}
                  />
                </div>

                {error ? <div style={{ fontSize: 11, color: K.bad, marginBottom: 12 }}>{error}</div> : null}

                <Btn onClick={submit} col={K.red} block>{loading ? 'Submitting...' : 'Submit Request'}</Btn>
              </>
            )}

            <button onClick={() => switchMode('login')} style={{ width: '100%', marginTop: 10, background: 'none', border: 'none', color: K.t3, cursor: 'pointer', fontSize: 11, fontFamily: K.fSans, padding: '8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <ArrowLeft size={14} color={K.t3} strokeWidth={1.75} /> Back to log in
            </button>
          </>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 24 }}>
              {['login', 'signup'].map((m) => (
                <button key={m} onClick={() => switchMode(m)} style={{ background: mode === m ? K.red : K.bg2, border: `1px solid ${mode === m ? K.red2 : K.bdr}`, color: mode === m ? K.t0 : K.t1, padding: '12px', borderRadius: 8, cursor: 'pointer', fontFamily: K.fSans, fontSize: 16, fontWeight: 600, letterSpacing: 0.3 }}>
                  {m === 'login' ? 'Log In' : 'Sign Up'}
                </button>
              ))}
            </div>

            {mode === 'signup' && (
              <div style={{ background: `${K.info}1a`, border: `1px solid ${K.info}44`, borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 11, color: K.t1, lineHeight: 1.5 }}>
                Please use your department email (<strong style={{ color: K.info }}>@cmfe.org</strong>) to sign up. No department email? Use <strong>Request access</strong> below.
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
              <input
                type="email"
                placeholder={mode === 'signup' ? 'name@cmfe.org' : 'Email'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                style={inputStyle()}
                autoComplete="email"
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                style={inputStyle()}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              />
            </div>

            {error ? <div style={{ fontSize: 11, color: K.bad, marginBottom: 12 }}>{error}</div> : null}

            <Btn onClick={submit} col={K.red} block>{loading ? 'Please wait...' : mode === 'login' ? 'Log In' : 'Create Account'}</Btn>

            {mode === 'login' ? (
              <button onClick={() => switchMode('reset')} style={{ width: '100%', marginTop: 10, background: 'none', border: 'none', color: K.t3, cursor: 'pointer', fontSize: 11, fontFamily: K.fSans, padding: '8px' }}>
                Forgot password?
              </button>
            ) : null}

            <div style={{ borderTop: `1px solid ${K.bdr}`, marginTop: 14, paddingTop: 14 }}>
              <div style={{ fontSize: 11, color: K.t3, textAlign: 'center', marginBottom: 8 }}>Don't have a department email?</div>
              <button onClick={() => switchMode('request')} style={{ width: '100%', background: 'transparent', border: `1px solid ${K.info}66`, color: K.info, padding: '11px', borderRadius: 8, cursor: 'pointer', fontFamily: K.fSans, fontSize: 13, fontWeight: 600, letterSpacing: 0.3 }}>
                Request access
              </button>
            </div>

            <button onClick={onClose} style={{ width: '100%', marginTop: 10, background: 'none', border: 'none', color: K.t3, cursor: 'pointer', fontSize: 11, fontFamily: K.fSans, padding: '8px' }}>
              Continue as guest
            </button>
          </>
        )}
      </div>
    </>
  );
}

function friendlyError(code, message) {
  switch (code) {
    case 'auth/invalid-email':           return 'Invalid email address.';
    case 'auth/user-not-found':          return 'No account found with that email.';
    case 'auth/wrong-password':          return 'Incorrect password.';
    case 'auth/email-already-in-use':    return 'An account with that email already exists.';
    case 'auth/weak-password':           return 'Password must be at least 6 characters.';
    case 'auth/invalid-credential':      return 'Email or password is incorrect.';
    case 'auth/too-many-requests':       return 'Too many attempts. Try again later.';
    case 'auth/network-request-failed':  return 'Network error reaching the auth server. Check connection or try a private window.';
    case 'auth/internal-error':          return 'Auth server returned an unexpected error. Try again in a minute.';
    case 'auth/operation-not-allowed':   return 'Email/password sign-in is disabled in Firebase. Contact admin.';
    case 'auth/user-disabled':           return 'This account has been disabled.';
    default:                             return code ? `Sign-in failed (${code}). ${message || ''}`.trim() : 'Something went wrong. Try again.';
  }
}
